import React, { useState, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { AutobizButton } from 'autobiz-strap';
import { Alert, Col, FormGroup, Label, Spinner } from 'reactstrap';
import { t } from 'autobiz-translate';
import PhoneInput from 'react-phone-number-input';
import { CountryCode } from 'libphonenumber-js/types';
import { updateUserInfoUseCase } from '../../../../hexagon/usecases/updateUserInfo/updateUserInfoUseCase';
import { getUserSelector } from '../../view-models-generators/userSelector';
import { UpdatableUserInfo } from '../../../../hexagon/models/userModel';

const availablePhonePrefix = [
    'FR',
    'ES',
    'IT',
    'PT',
    'GB',
    'DE',
    'BE',
    'LU',
    'AT',
    'NL',
] as CountryCode[];

const phonePrefixCountryLabel = {
    FR: t('france'),
    ES: t('spain'),
    IT: t('italy'),
    PT: t('portugal'),
    GB: t('greatBritain'),
    DE: t('germany'),
    BE: t('belgium'),
    LU: t('luxembourg'),
    AT: t('austria'),
    NL: t('netherlands'),
};
let defaultPhonePrefix: CountryCode = 'FR';

const validationSchema: Yup.SchemaOf<any> = Yup.object().shape({
    email: Yup.string().email().required(),
    phone: Yup.string().required().min(10),
    language: Yup.string().required(),
});

const Profile: React.FC = () => {
    const dispatch = useDispatch();
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const { user, updateStatus } = useSelector(getUserSelector);
    const { email, phone, language } = user;

    useEffect(() => {
        if (['succeeded', 'failed'].includes(updateStatus)) setAlertOpen(true);
    }, [updateStatus]);

    const validate = (values: any): Promise<any> =>
        validationSchema
            .validate(values, { abortEarly: false })
            .then(() => setCanSubmit(true))
            .catch((error: Yup.ValidationError) => {
                const errors: { [value: string]: string } = {};
                error.inner.forEach((e) => {
                    if (e.path) {
                        errors[e.path] = e.message;
                    }
                });
                setCanSubmit(false);

                return errors;
            });

    const onSubmit = async (props: Partial<UpdatableUserInfo>) => {
        let userInfo = {
            id: user.id,
        };
        if (email !== props.email) {
            userInfo = { ...userInfo, ...{ email: props.email } };
        }

        if (phone !== props.phone) {
            userInfo = { ...userInfo, ...{ phone: props.phone } };
        }
        dispatch(updateUserInfoUseCase(userInfo));
        setAlertOpen(false);
    };

    if (availablePhonePrefix.includes(user.country as CountryCode)) {
        defaultPhonePrefix = user.country.toUpperCase() as CountryCode;
    }

    return (
        <>
            {updateStatus === 'succeeded' && (
                <Alert
                    color="success"
                    className="mt-4"
                    fade
                    isOpen={alertOpen}
                    toggle={() => setAlertOpen(false)}
                >
                    {t('updateUserProfileSucceed')}
                </Alert>
            )}

            {updateStatus === 'failed' && (
                <Alert
                    color="danger"
                    className="mt-4"
                    fade
                    isOpen={alertOpen}
                    toggle={() => setAlertOpen(false)}
                >
                    {t('updateUserProfileFailed')}
                </Alert>
            )}

            <p>{t('explanationProfilePage')}</p>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={{ email, phone, language }}
                render={({ handleSubmit, pristine }) => (
                    <form onSubmit={handleSubmit} className="mt-5">
                        <div>
                            <Field
                                name="email"
                                render={({ input, meta }) => (
                                    <FormGroup
                                        className={
                                            meta.error && meta.touched
                                                ? 'row form-group-error'
                                                : 'row'
                                        }
                                    >
                                        <Col sm={6} lg={8}>
                                            <Label>{t('email')}</Label>
                                        </Col>

                                        <Col sm="6" lg={4} className="userInputs">
                                            <input
                                                type="text"
                                                {...input}
                                                disabled={updateStatus === 'loading'}
                                                value={input.value}
                                                className={`form-control text-right ${
                                                    meta.error && meta.touched && 'error-control'
                                                }`}
                                            />
                                        </Col>
                                    </FormGroup>
                                )}
                            />
                        </div>
                        <div>
                            <Field
                                name="phone"
                                render={({ input, meta }) => (
                                    <FormGroup
                                        className={
                                            meta.error && meta.touched
                                                ? 'row form-group-error'
                                                : 'row'
                                        }
                                    >
                                        <Col sm={6} lg={8}>
                                            <Label>{t('phoneNumber')}</Label>
                                        </Col>

                                        <Col sm="6" lg={4} className="userInputs">
                                            <PhoneInput
                                                international={false}
                                                defaultCountry={defaultPhonePrefix}
                                                disabled={updateStatus === 'loading'}
                                                labels={phonePrefixCountryLabel}
                                                countries={availablePhonePrefix}
                                                {...input}
                                                value={input.value}
                                                className={`form-control text-right ${
                                                    meta.error && meta.touched && 'error-control'
                                                }`}
                                            />
                                        </Col>
                                    </FormGroup>
                                )}
                            />
                        </div>
                        <AutobizButton
                            type="submit"
                            color="primary"
                            disabled={!canSubmit || updateStatus === 'loading' || pristine}
                            className={`mx-auto mt-5 ${!canSubmit || updateStatus === 'loading'}`}
                        >
                            {updateStatus === 'loading' ? <Spinner /> : t('valider')}
                        </AutobizButton>
                    </form>
                )}
            />
        </>
    );
};
export default Profile;
